import { memone } from '@deqo/foundation';
import { bindCommand, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { PointTupleType } from '../../../concepts/objects/PointTupleType';
import { PointType } from '../../../concepts/objects/PointType';
import { Matrix } from '../../../platform/geom/Matrix';
import { Path } from '../../../platform/geom/Path';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AbstractPhysical } from '../../canvas/entities/AbstractPhysical';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import {
    Figure,
    Physical,
    PotentialInteraction,
    RelativeTo,
    TransformAction,
    TransformHints,
    TransformScope
} from '../../canvas/entities/Physical';
import { RepeatInfo, Repeatable } from '../../canvas/entities/Repeatable';
import { ToolSettings } from '../../canvas/tooling/ToolBehavior';
import { ConnectTarget } from './ConnectTarget';
import { ConnectInfo, Connectable, DisconnectInfo } from './Connectable';
import { connectorConnect, connectorDisconnect, connectorRotate } from './ConnectorCommands';
import { ConnectorCapStyle, ConnectorMode, ConnectorStrokeStyle } from './ConnectorEnums';
import { ConnectorLabelField } from './ConnectorLabelField';
import { ConnectorLayout } from './ConnectorLayout';
import { connectorPropertyLayout } from './ConnectorPropertyLayout';

// const spliceNodes = (nodes: readonly Point[], mpt: Point): readonly Point[] => {
//     // Find nearest two nodes
//     let [a, b] = Array.from(nodes).sort((a, b) => a.distanceTo(mpt) - b.distanceTo(mpt));
//     if (nodes.indexOf(a) > nodes.indexOf(b)) {
//         [a, b] = [b, a];
//     }
//     const n = new Line(a, b).nearestPointTo(mpt);
//     return nodes.flatMap(p => p === a ? [a, n] : p);
// };

const mutation = defineCommand(
    'connectorMutation',
    null,
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const connectorDef = {
    name: 'Connector',
    implements: [Physical, AutoBringToFront, Connectable, Repeatable],
    propertyLayout: connectorPropertyLayout,
    mutation,
};

@component(connectorDef)
export class Connector extends AbstractPhysical implements Connectable, Repeatable {

    @property({ type: PointTupleType, default: [Point.zero, Point.zero] })
    public nodes: readonly Point[];

    @property({ type: 'string' })
    public label: string;

    @property({ type: 'number', default: ConnectorMode.direct, style: true })
    public mode: number;

    @property({ type: 'number', default: 3, style: true })
    public strokeWidth: number;

    @property({ type: 'number', default: ConnectorStrokeStyle.solid, style: true })
    public strokeStyle: number;

    @property({ type: 'number', default: ConnectorCapStyle.none, style: true })
    public startCap: number;

    @property({ type: 'number', default: ConnectorCapStyle.none, style: true })
    public endCap: number;

    @property({ type: 'string', default: Colors.text, style: true })
    public color: string;

    @property({ type: 'string', name: '@from' })
    public fromId?: string;

    @property({ type: PointType, default: new Point(.5, .5) })
    public fromOffset: Point;

    @property({ type: 'number', default: 0 })
    public fromRotation: number;

    @property({ type: 'string', name: '@to' })
    public toId?: string;

    @property({ type: PointType })
    public toOffset: Point;

    @property({ type: 'number', default: 0 })
    public toRotation: number;

    public get boundary(): boolean {
        return true;
    }

    // eslint-disable-next-line max-len
    @memoUsing('nodes', 'mode', 'strokeWidth', 'startCap', 'endCap', 'fromId', 'fromOffset', 'fromRotation', 'toId', 'toOffset', 'toRotation', 'label')
    public get layout() {
        return ConnectorLayout(this);
    }

    @memoUsing('layout')
    public get uiFields() {
        return [ConnectorLabelField(this)];
    }

    protected get parentTransform(): Matrix {
        const f = this.computeFrame(this.nodes);
        return Matrix.translate(f.left, f.top);
    }

    protected get localBounds(): Rect {
        const { w, h } = this.computeFrame(this.nodes).size();
        return new Rect(0, 0, w, h);
    }

    public side(mode: 'at' | 'opposite', id: string) {
        if ((id === this.fromId && mode === 'at') || (id === this.toId && mode === 'opposite')) {
            return {
                type: 'from',
                node: this.nodes[0],
                offset: this.fromOffset,
            };
        }
        else {
            return {
                type: 'to',
                node: this.nodes[this.nodes.length - 1],
                offset: this.toOffset,
            };
        }
    }

    public queryConnect(node: number, mpt: Point, target: ConnectTarget): ConnectInfo {
        if (node !== 0 && node !== this.nodes.length - 1) return null;

        // If both nodes are inside the bounds of the target, we can't connect.
        const fg = this.createFigure();
        const tb = target.bounds(RelativeTo.Model);
        if (fg.every(x => tb.contains(x))) return null;

        const input = [this.id, node === 0 ? 'start' : 'end', target.id, target.toRelative(mpt)];
        const command = bindCommand(connectorConnect, input);

        return { command, target };
    }

    public queryDisconnect(node: number): DisconnectInfo {
        if (node !== 0 && node !== this.nodes.length - 1) return null;

        const command = bindCommand(connectorDisconnect, [this.id, node === 0 ? 'start' : 'end']);
        return { command };
    }

    public queryRepeat(): RepeatInfo {
        return {
            toolId: 'connectorTool',
            toolSettings: ToolSettings.fromStyleProperties(this),
        };
    }

    public createFigure(): Figure {
        return this.localModelTransform().apply(this.nodes);
    }

    public queryTransformActions(singular: boolean): TransformAction[] {

        const [start, end] = this.nodes;
        const { startNormal, endNormal } = this.layout;
        const { fromId, toId, mode } = this;

        if (mode !== ConnectorMode.smooth) {
            return [];
        }

        return [
            ...(!fromId ? [{
                key: 'fromRotate',
                position: start,
                orientation: startNormal.multiply(50),
                command: bindCommand(connectorRotate, [this.id, 'start']),
                visibility: 'proximity' as const,
                style: { type: 'button', icon: 'deqo://icons/d2/connector-rotate' },
            }] : []),
            ...(!toId ? [{
                key: 'toRotate',
                position: end,
                orientation: endNormal.multiply(50),
                command: bindCommand(connectorRotate, [this.id, 'end']),
                visibility: 'proximity' as const,
                style: { type: 'button', icon: 'deqo://icons/d2/connector-rotate' },
            }] : []),
        ];
    }

    public queryTransformHints(singular: boolean): TransformHints {
        const attached = !!this.fromId || !!this.toId;
        return {
            translate: false,
            rotate: !singular,
            scaleX: !singular,
            scaleY: !singular,
            tweaks: !!singular ? this.nodes.map((_, i) => ({ offset: i })) : null,
            visible: true,
        };
    }

    public applyOffsets() {
        if (!this.alive || !this.mounted()) return;
        if (this.fromId === this.toId) return;
        const targets = this.relations('attachee', ConnectTarget);
        const from = targets.find(t => t.id === this.fromId);
        const to = targets.find(t => t.id === this.toId);
        const f = this.createFigure();
        const a = from?.bounds(RelativeTo.Model).pointAt(this.fromOffset) ?? f[0];
        const b = to?.bounds(RelativeTo.Model).pointAt(this.toOffset) ?? f[f.length - 1];
        this.setFromFigure([a, ...f.slice(1, f.length - 1), b]);
    }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        // Figures are always model level, so we need to apply the inverse transform of the parent to localize it.
        const mlt = this.modelLocalTransform();
        from = mlt.apply(from);
        to = mlt.apply(to);
        this.nodes = Array.from(to);
        return this;
    }

    public area(to: RelativeTo | Physical = RelativeTo.Parent): Rect {
        return Rect.fromPoints(
            this.transform(to).apply(this.layout.base.inflate(this.strokeWidth / 2))
        );
    }

    public outline(to?: RelativeTo | Physical): Polyline {
        return this.bounds(to).toPolyline();
    }

    public path(to?: RelativeTo | Physical): Path {
        return this.outline(to).toPath();
    }

    public size(): Point {
        return this.computeFrame(this.nodes).size();
    }

    public test(mpt: Point): boolean {
        const { base, start, end, body, label } = this.layout;
        const rpt = this.toRelative(mpt);

        // DebugOverlay.polyline('base', this.transform(RelativeTo.Model).apply(base), 'blue', true);
        // DebugOverlay.polyline('head', this.transform(RelativeTo.Model).apply(head.bounds.inflate(this.strokeWidth)), 'blue', true);
        // DebugOverlay.path('body', this.transform(RelativeTo.Model).apply(body));

        if (base.contains(rpt)) {
            if (label?.contains(rpt)) return true;
            if (start?.contains(rpt, this.strokeWidth)) return true;
            if (end?.contains(rpt, this.strokeWidth)) return true;
            if (body?.contains(rpt, this.strokeWidth + 4)) return true;
        }

        return false;
    }

    public testInteraction({ type }: PotentialInteraction): [boolean, string] {
        if (type !== 'invoke') {
            return [true, 'default'];
        }
        return [false, 'none'];
    }

    public toTuple(to: RelativeTo | Physical = RelativeTo.Parent) {
        if (to !== RelativeTo.Parent) {
            // Compute relative transform and decompose into rotation and position
            const rmt = this.localModelTransform();
            // Compute the tuple for this element
            const nodes = PointTupleType.serialize(this.nodes.map(x => rmt.apply(x)));
            const data = super.toTuple();
            // Overwrite the position and rotation data
            Object.assign(data[2], { nodes });
            return data;
        }
        else {
            // If relative to parent, just return data as-is since that's how it's stored.
            return super.toTuple();
        }
    }

    public toShallowTuple(to: RelativeTo | Physical = RelativeTo.Parent) {
        if (to !== RelativeTo.Parent) {
            // Compute relative transform and decompose into rotation and position
            const rmt = this.transform(to);
            // Compute the tuple for this element
            const nodes = PointTupleType.serialize(this.nodes.map(x => rmt.apply(x)));
            const data = super.toShallowTuple();
            // Overwrite the position and rotation data
            Object.assign(data[2], { nodes });
            return data;
        }
        else {
            // If relative to parent, just return data as-is since that's how it's stored.
            return super.toShallowTuple();
        }
    }

    @memone()
    private computeFrame(ps: readonly Point[]): Rect {
        return Rect.fromPoints(ps);
    }
}
